/**
 * Derivacao CLIENT-SIDE do nivel NANO (etapas internas de uma execucao).
 *
 * CAUSA RAIZ do bug "ninguem aqui agora" com execucao em andamento (print de
 * 08/09, "0 de 7 empresas concluidas" e todos os nos vazios): a UNICA ancora que
 * a tela usava para posicionar as empresas em processamento era `nano.atual` — o
 * par (empresa, modulo) ABERTO mais recente que o mon devolve. So que o mon conta
 * como "em voo" (em_processamento da fila) tambem a empresa cujo documento de
 * unidade esta aberto SEM nenhum evento de modulo aberto naquele instante
 * (`fila_situacao` no mon: doc de `bot_unit_result` com status null tambem e' voo)
 * — empresa entre um `concluido` e o proximo `iniciado`, ou trabalhando antes do
 * 1o modulo. Nesse estado o backend manda `atual: null` com `em_processamento`
 * cheio, e as empresas executando nao caiam em NENHUM no.
 *
 * A correcao e' na fonte da derivacao, com fallbacks honestos e nesta ordem:
 *   1. `atual` aponta um modulo da trilha → as empresas em voo estao la;
 *   2. senao, o primeiro modulo com `situacao === 'em_andamento'` (o sinal POR
 *      MODULO que o proprio /nano calcula dos eventos abertos);
 *   3. senao, NADA e' inventado: as empresas vao para um balde VISIVEL
 *      "Etapa nao informada" — melhor dizer "o bot nao informou a etapa" do que
 *      fingir que estao na etapa 1 (estar entre a 3 e a 4 e' o caso tipico).
 */

import type { IModuloNano } from '../api';
import type { IEmpresaComStatus } from './derivarProcessos';

/** Subconjunto do modulo que a alocacao precisa (facilita fixture nos testes). */
export type ModuloParaAlocacao = Pick<IModuloNano, 'modulo_id' | 'situacao'>;

export interface OcupacaoNano {
  /** Por INDICE do modulo (lista ja ordenada): quantas empresas estao AQUI agora. */
  porIndice: number[];
  /** Indice do modulo-ancora das empresas em voo, ou null (balde/ninguem em voo). */
  indiceEmVoo: number | null;
  /** Empresas em voo SEM etapa identificavel — vao para o balde visivel, nunca somem. */
  semEtapa: number;
}

/**
 * Onde estao as empresas em processamento, na trilha de modulos.
 *
 * `emProcessamento` null = fila ainda nao carregada: se ha ancora, mostra 1 (a
 * empresa do proprio `atual`), como a tela sempre fez; sem ancora, nada e' inventado.
 */
export const ocupacaoEmVoo = (
  modulos: ModuloParaAlocacao[],
  atual: { modulo_id: number } | null,
  emProcessamento: string[] | null,
): OcupacaoNano => {
  const zeros = modulos.map(() => 0);

  let indice: number | null = null;
  if (atual) {
    const doAtual = modulos.findIndex(m => m.modulo_id === atual.modulo_id);
    if (doAtual >= 0) indice = doAtual;
  }
  if (indice === null) {
    const emAndamento = modulos.findIndex(m => m.situacao === 'em_andamento');
    if (emAndamento >= 0) indice = emAndamento;
  }

  let qtd = 0;
  if (emProcessamento) qtd = emProcessamento.length;
  else if (indice !== null) qtd = 1;
  if (qtd === 0) return { porIndice: zeros, indiceEmVoo: null, semEtapa: 0 };
  if (indice === null) return { porIndice: zeros, indiceEmVoo: null, semEtapa: qtd };

  const porIndice = zeros.slice();
  porIndice[indice] = qtd;
  return { porIndice, indiceEmVoo: indice, semEtapa: 0 };
};

/**
 * Em voo cuja ancora INDIVIDUAL e' conhecida — as unicas que podem entrar no
 * "adiante" da bolinha (correcao M1 da revisao 09/09): a ancora do grupo
 * (`atual`/primeiro `em_andamento`) localiza UMA posicao, nao a fila inteira —
 * empresa em voo ATRAS da ancora contada como "ja passou" era chute. Ancora
 * individual conhecida:
 *   - a empresa que o `atual` NOMEIA (`codi_emp`) esta' naquele modulo; ou
 *   - ha' ancora e UMA UNICA empresa em voo (a posicao so' pode ser dela).
 * Qualquer outra em voo fica fora (a nota no subtitulo do popup explica).
 */
export const emVooComAncoraConhecida = (
  emVoo: IEmpresaComStatus[],
  indiceEmVoo: number | null,
  atualCodiEmp: string | null,
): IEmpresaComStatus[] => {
  if (indiceEmVoo === null) return [];
  if (atualCodiEmp !== null) return emVoo.filter(c => c.codiEmp === atualCodiEmp);
  return emVoo.length === 1 ? emVoo : [];
};

/**
 * Empresas que JA PASSARAM pelo modulo de indice `indice` (bolinha acima do no,
 * pedido 08/09). Numero = lista, e a lista so' afirma passagem COM EVIDENCIA
 * (correcoes A1/M1 da revisao 09/09):
 *   - entregues com SUCESSO no bot inteiro (entregue com ERRO fica FORA de todos
 *     os modulos — nao sabemos em qual etapa falhou, afirmar passagem por todas
 *     era mentira);
 *   - em voo cuja ancora INDIVIDUAL e' conhecida (`emVooComAncoraConhecida`) e
 *     esta DEPOIS deste modulo na ordem da trilha.
 * Em voo sem posicao conhecida e o balde "Etapa nao informada" NAO entram — a
 * nota no subtitulo do popup deixa as duas exclusoes explicitas. Dedup por
 * codi_emp com a entrega vencendo (status final > posicao transitoria).
 */
export const jaPassaramPeloModulo = (
  indice: number,
  indiceEmVoo: number | null,
  emVoo: IEmpresaComStatus[],
  entregues: IEmpresaComStatus[],
  atualCodiEmp: string | null = null,
): IEmpresaComStatus[] => {
  const vistos = new Set<string>();
  const ancorados = emVooComAncoraConhecida(emVoo, indiceEmVoo, atualCodiEmp);
  const adiante = indiceEmVoo !== null && indiceEmVoo > indice ? ancorados : [];
  // Entregue com erro NAO passou comprovadamente por este modulo (A1).
  const passaram = entregues.filter(c => c.st !== 'err');
  return [...passaram, ...adiante].filter(c => {
    if (vistos.has(c.codiEmp)) return false;
    vistos.add(c.codiEmp);
    return true;
  });
};

/**
 * Recorte global/regime sobre listas vindas da EXECUCAO (regra de ouro: filtrar
 * antes de agregar). `permitidos` = codi_emps que sobrevivem aos filtros globais +
 * regime do contexto (derivados do /resumo); `conhecidos` = todos os codi_emps que
 * o /resumo conhece.
 *
 * KEEP-RULE DELIBERADA (comentario corrigido na revisao 09/09 — antes alegava
 * "cache de 300s", sugerindo janela transitoria): empresa que o /resumo NAO
 * conhece passa pelo recorte PERMANENTEMENTE, nao so' durante uma corrida de
 * cache. Dado vivo da execucao nunca some da tela — e o alcance inclui empresa
 * INATIVA/fora de vigencia presente na execucao (o /resumo default nao a traz).
 * O popup rotula essas empresas com a tag "fora do resumo" (`marcarForaDoResumo`)
 * em vez de exibi-las como se fossem recortaveis.
 */
export const filtrarPorRecorte = <T extends { codiEmp: string }>(
  lista: T[],
  permitidos: Set<string> | null,
  conhecidos: Set<string>,
): T[] =>
  permitidos ? lista.filter(c => permitidos.has(c.codiEmp) || !conhecidos.has(c.codiEmp)) : lista;

/**
 * Marca (SEM cortar) quem esta fora do /resumo — o rotulo visivel da keep-rule de
 * `filtrarPorRecorte`: a empresa preservada por nao ser conhecida ganha
 * `foraDoResumo: true` e o popup exibe a tag discreta "fora do resumo".
 * Quem o /resumo conhece mantem a MESMA referencia (zero mudanca).
 */
export const marcarForaDoResumo = <T extends { codiEmp: string }>(
  lista: T[],
  conhecidos: Set<string>,
): (T & { foraDoResumo?: boolean })[] =>
  lista.map(c => (conhecidos.has(c.codiEmp) ? c : { ...c, foraDoResumo: true }));
